"use client";

import React, { useState, useEffect } from "react";

interface BasicInputsProps {
    /** $ Down Payment */
    downPayment: number;
    setDownPayment: (v: number) => void;

    /** $ Target total monthly payment */
    totalMonthly: number;
    setTotalMonthly: (v: number) => void;

    /** Loan term in years */
    loanTerm: number;
    setLoanTerm: (v: number) => void;

    /** Interest rate (%) */
    mortgageRate: number;
    setMortgageRate: (v: number) => void;
}

// keeps the function in the same file, but now it never crashes
const fmt = (v: number | string | null | undefined) => `${v ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const BasicInputs: React.FC<BasicInputsProps> = ({
    downPayment,
    setDownPayment,
    totalMonthly,
    setTotalMonthly,
    loanTerm,
    setLoanTerm,
    mortgageRate,
    setMortgageRate,
}) => {
    /* local text states to support comma formatting */
    const [dpInput, setDpInput] = useState("0");
    const [tmInput, setTmInput] = useState("0");

    /* sync controlled values → formatted inputs */
    useEffect(() => setDpInput(fmt(downPayment)), [downPayment]);
    useEffect(() => setTmInput(fmt(totalMonthly)), [totalMonthly]);

    /* accordion open / close */
    const [isOpen, setIsOpen] = useState(true);

    return (
        <div className="border border-gray-300 overflow-hidden shadow 2xl:w-[900px]">
            {/* header */}
            <button
                type="button"
                className="w-full text-left bg-[#202358] text-white px-4 py-3 flex items-center justify-between hover:bg-blue 2xl:h-[42px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <h5 className="text-lg font-bold">BASIC</h5>
                <span className="text-xl">{isOpen ? "−" : "+"}</span>
            </button>

            {/* body */}
            <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                    isOpen ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                }`}
            >
                <div className="bg-white p-3 2xl:w-[898px]">
                    {/* ── DOWN PAYMENT ─────────────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="downPayment"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[117px]"
                                >
                                    Down Payment: $
                                </label>
                                <input
                                    id="downPayment"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={dpInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            setDownPayment(Number(raw));
                                            setDpInput(fmt(raw));
                                        }
                                    }}
                                    onBlur={() => setDpInput(fmt(downPayment))}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={1_000_000}
                                step={1_000}
                                value={downPayment}
                                onChange={(e) => setDownPayment(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>$0</span>
                                <span>$10K</span>
                                <span>$100K</span>
                                <span>$1M</span>
                            </div>
                        </div>
                    </div>

                    {/* ── TOTAL MONTHLY PAYMENT ───────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="totalMonthly"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[170px]"
                                >
                                    Total Monthly Payment: $
                                </label>
                                <input
                                    id="totalMonthly"
                                    type="text"
                                    inputMode="numeric"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={tmInput}
                                    onChange={(e) => {
                                        const raw = e.target.value.replace(/,/g, "");
                                        if (/^\d*$/.test(raw)) {
                                            setTotalMonthly(Number(raw));
                                            setTmInput(fmt(raw));
                                        }
                                    }}
                                    onBlur={() => setTmInput(fmt(totalMonthly))}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={100_000}
                                step={100}
                                value={totalMonthly}
                                onChange={(e) => setTotalMonthly(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>$0</span>
                                <span>$1K</span>
                                <span>$10K</span>
                                <span>$100K</span>
                            </div>
                        </div>
                    </div>

                    {/* ── TERM (YEARS) ────────────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="loanTerm"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[90px]"
                                >
                                    Term (Years):
                                </label>
                                <input
                                    id="loanTerm"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={loanTerm}
                                    onChange={(e) => setLoanTerm(Number(e.target.value))}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={40}
                                step={1}
                                value={loanTerm}
                                onChange={(e) => setLoanTerm(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0</span>
                                <span>13</span>
                                <span>26</span>
                                <span>40</span>
                            </div>
                        </div>
                    </div>

                    {/* ── INTEREST RATE (%) ──────────────────────── */}
                    <div>
                        <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:gap-4">
                            <div className="2xl:w-[367px] 2xl:flex 2xl:justify-end">
                                <label
                                    htmlFor="mortgageRate"
                                    className="w-32 font-medium text-black text-[15px] 2xl:w-[106px]"
                                >
                                    Interest Rate: %
                                </label>
                                <input
                                    id="mortgageRate"
                                    type="number"
                                    className="border px-3 py-1 rounded w-36 bg-[#f6f9ff] text-[14px] 2xl:w-[122px]"
                                    value={mortgageRate}
                                    onChange={(e) => setMortgageRate(Number(e.target.value))}
                                />
                            </div>

                            <input
                                type="range"
                                min={0}
                                max={32}
                                step={0.125}
                                value={mortgageRate}
                                onChange={(e) => setMortgageRate(Number(e.target.value))}
                                className="w-full 2xl:w-[506px] accent-blue"
                            />
                        </div>
                        <div className="flex justify-end text-xs text-black mt-1">
                            <div className="pb-10 flex justify-between w-full max-w-md 2xl:pb-0">
                                <span>0%</span>
                                <span>12%</span>
                                <span>24%</span>
                                <span>32%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BasicInputs;
